import { useEffect, useState } from "react";
import { IonContent, IonPage } from "@ionic/react";

import { ActionBar } from "../components/ActionBar";
import { I } from "../components/icons";
import { Mark } from "../components/Mark";
import { NavRow, RowGroup } from "../components/Rows";
import { Screen } from "../components/Shell";
import { Tag } from "../components/Tag";
import { useKaya } from "../store/kayaStore";
import "./Login.css";

/** How often the screen asks again on its own. Long enough not to hammer the server while a
 *  phone sits on a desk, short enough that an approval lands before anyone gives up and reloads. */
const POLL_MS = 30_000;

function clock(ms: number): string {
  return new Date(ms).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
}

/**
 * The screen for an account that exists but is not allowed in yet.
 *
 * It is not the sign-in screen: the password was right, and asking for it again would suggest it
 * was not. The session is kept, and this polls quietly so that being approved is something the
 * person sees happen rather than something they have to go and check.
 */
export default function Pending() {
  const { user, refreshUser, logout } = useKaya();
  const [checking, setChecking] = useState(false);
  const [checkedAt, setCheckedAt] = useState<number | null>(null);

  const check = async () => {
    setChecking(true);
    try {
      await refreshUser();
      setCheckedAt(Date.now());
    } catch {
      // offline, or the server is down — the next tick tries again
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    const iv = window.setInterval(() => void check(), POLL_MS);
    return () => window.clearInterval(iv);
    // `check` closes over nothing that changes; re-arming on every render would reset the clock
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const suspended = user?.status === "suspended";

  return (
    <IonPage>
      <IonContent>
        <Screen>
          <div className="auth">
            <div className="auth-head">
              <div className="auth-mark">
                <Mark />
              </div>
              <div>
                <h1 className="auth-title">{suspended ? "This account is closed" : "Waiting for approval"}</h1>
                <p className="auth-sub">
                  {suspended
                    ? "Access to this account has been turned off. Nothing has been deleted."
                    : "Kaya opens by itself once the owner approves your account. You can close the app — your place is kept."}
                </p>
              </div>
            </div>

            <RowGroup>
              <NavRow
                kind="action"
                icon={I.user}
                title={user?.name || user?.email || "–"}
                subtitle={user?.email}
                action={suspended ? <Tag tone="est">suspended</Tag> : <Tag tone="planned">pending</Tag>}
              />
              <NavRow
                kind="value"
                icon={I.clock}
                title={checkedAt ? `Last checked ${clock(checkedAt)}` : "Not checked yet"}
                subtitle={
                  checkedAt && !suspended
                    ? "No change yet · checks again every 30 s"
                    : "Checks again every 30 s"
                }
              />
            </RowGroup>
          </div>

          <ActionBar
            primary={{
              label: checking ? "Checking…" : "Check now",
              icon: I.refresh,
              onClick: () => void check(),
              busy: checking,
            }}
            secondary={{ label: "Sign out", onClick: logout }}
          />
        </Screen>
      </IonContent>
    </IonPage>
  );
}
